// Platform-agnostic personal-task list for the Tasks surface.
//
// Tasks are DeviceGroup-only personal state (§2.10 `0x92 TASK_UPSERT` /
// `0x93 TASK_DELETE`) mirrored across the user's own devices. They are
// stored as flat `TaskRecord` rows keyed on the stable hex `task_id`;
// upserts are last-writer-wins, deletes remove the row. This hook reads
// the host-supplied `TasksPersister` (Dexie liveQuery on web, the
// AsyncStorage slice on desktop) and returns a live array sorted by the
// fractional `position` index (lex-sortable) so manual ordering is
// stable across devices.
//
// Host-neutral: it reads/writes nothing itself. When no persister is
// supplied (a host that hasn't wired tasks yet) it returns a stable
// empty array.

import { useEffect, useMemo, useState } from 'react';

import type { TaskRecord, TasksPersister } from './persistenceAdapters';

const EMPTY: readonly TaskRecord[] = Object.freeze([]);

/**
 * Live array of the user's personal tasks, sorted by `position`.
 *
 * @param persister  Backing store; when omitted the hook returns a
 *                   frozen empty array (no subscription).
 */
export function useTasks(persister?: TasksPersister): readonly TaskRecord[] {
  const [rows, setRows] = useState<readonly TaskRecord[]>(EMPTY);

  useEffect(() => {
    if (!persister) {
      setRows(EMPTY);
      return;
    }

    let cancelled = false;

    if (persister.subscribe) {
      const unsubscribe = persister.subscribe((next) => {
        if (!cancelled) setRows(next);
      });
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    void persister
      .list()
      .then((next) => {
        if (!cancelled) setRows(next);
      })
      .catch(() => {
        if (!cancelled) setRows(EMPTY);
      });

    return () => {
      cancelled = true;
    };
  }, [persister]);

  return useMemo(() => {
    if (rows.length === 0) return EMPTY;
    // Copy before sort — the source array may be frozen / shared.
    return [...rows].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
  }, [rows]);
}
